/**
 * The sound model: every number the audio graph needs, as pure functions.
 *
 * ## Why this file exists at all
 *
 * Web Audio is a graph of opaque native nodes. You cannot read a `GainNode`'s output, you cannot
 * assert that a filter is where you think it is, and none of it exists under Vitest. If the
 * mapping from *flight state* to *sound* lived inside the graph code it would be untestable, and
 * "the stall buffet is audible before the horn" would be a claim rather than a fact.
 *
 * So the mapping lives here, in functions that take numbers and return numbers, and
 * `graph/*.ts` does nothing but push those numbers at `AudioParam`s. Everything interesting is
 * therefore pinned by `model.test.ts`: the buffet *is* asserted to arrive before the horn, the
 * Doppler ratio *is* asserted to rise on approach and fall on departure, mute *is* asserted to
 * silence the master bus.
 *
 * ## Conventions
 *
 * SI in, dimensionless out — CLAUDE.md's units rule applies here as everywhere else. Frequencies
 * are the one exception and are returned in hertz, because that is the unit `AudioParam` takes.
 * Gains are linear amplitude (not dB), because that is what `GainNode.gain` takes.
 *
 * `updateFixed` / `updateFrame`: neither. These are called from the audio system's frame update,
 * which runs once per rendered frame; they are leaf functions and allocate nothing.
 */

import { clamp, clamp01, smoothstep } from '../core/math.js';

// ---------------------------------------------------------------------------------------------
// Piston engine
// ---------------------------------------------------------------------------------------------

/**
 * The engine's fundamental note [Hz] — its *firing* frequency, not its shaft speed.
 *
 * A four-stroke engine fires each cylinder once every two crankshaft revolutions, so a
 * four-cylinder four-stroke produces two power strokes per revolution and its fundamental is
 * `rpm/30`. That is the note you actually hear: 90 Hz at 2700 rpm, 23 Hz at a 700 rpm idle.
 *
 * Getting this from the cylinder count rather than hard-coding 2/rev is what lets a six-cylinder
 * Continental or a four-cylinder Lycoming sound like themselves from the same synth.
 */
export function firingFundamentalHz(rpm: number, cylinders: number, strokes = 4): number {
  const firingsPerRevolution = cylinders / (strokes / 2);
  return (Math.max(0, rpm) / 60) * firingsPerRevolution;
}

/**
 * The rate at which the engine completes a whole firing cycle [Hz] — the *lope*.
 *
 * A four-stroke needs two revolutions to fire every cylinder once, and no two cylinders make
 * exactly the same power, so the engine has a slow beat at `rpm/120` on top of its firing note.
 * At a 700 rpm idle that is 5.8 Hz, which is precisely the chug you hear on the ramp. By 2400 rpm
 * it is 20 Hz and has stopped being a rhythm and become part of the timbre — which is why
 * {@link lopeDepth} fades it out rather than leaving it running.
 */
export function engineCycleHz(rpm: number, strokes = 4): number {
  return Math.max(0, rpm) / 60 / (strokes / 2);
}

/** Propeller blade-pass frequency [Hz]: the tone a fixed-pitch prop makes at the disc. */
export function bladePassHz(rpm: number, blades: number): number {
  return (Math.max(0, rpm) / 60) * blades;
}

/**
 * How close the engine is to idling, 0…1.
 *
 * Drives the lope depth and the harmonic balance. 1 at or below the idle stop, 0 at about a
 * third of the way to rated — a hot idle is still lumpy, a cruise-power engine never is.
 */
export function idleness(rpm: number, idleRpm: number, ratedRpm: number): number {
  const span = Math.max(1, (ratedRpm - idleRpm) * 0.35);
  return 1 - clamp01((rpm - idleRpm) / span);
}

/** Amplitude-modulation depth of the idle lope, 0…1. Deep on the ramp, gone by cruise. */
export function lopeDepth(idlenessValue: number): number {
  return 0.52 * clamp01(idlenessValue);
}

/**
 * Engine load, 0…1 — the fraction of rated power actually being produced.
 *
 * Load, not throttle. A wide-open throttle at 8000 ft is making 70 % power and should sound like
 * it; a closed throttle in a dive is turning 2400 rpm and making almost nothing, which is the
 * hollow windmilling sound every pilot recognises on the way down final. Throttle alone gets both
 * of those backwards.
 */
export function engineLoad(powerW: number, ratedPowerW: number): number {
  if (!(ratedPowerW > 0)) return 0;
  return clamp01(powerW / ratedPowerW);
}

/** How many harmonics of the firing note the engine voice runs. */
export const ENGINE_HARMONIC_COUNT = 8;

/**
 * Relative amplitude of harmonic `index` (0 = the fundamental), 0…1.
 *
 * Two shaping terms, and both are audible:
 *
 *   - **Load** brightens. An unloaded engine is a soft `1/n` rolloff, close to a triangle wave;
 *     a loaded one is nearer `1/√n`, close to a sawtooth, which is what makes a climb sound
 *     *strained* rather than merely louder. Interpolating the exponent between the two is a
 *     one-line way to get a continuous version of that.
 *   - **Idle** hollows out the middle. At a lumpy idle the second and third harmonics drop
 *     relative to the fundamental and the upper ones stay, which reads as "chuffing" rather than
 *     "droning".
 */
export function harmonicGain(index: number, load: number, idlenessValue: number): number {
  const n = index + 1;
  const exponent = 1.15 - 0.5 * clamp01(load);
  let gain = Math.pow(n, -exponent);
  if (n === 2 || n === 3) gain *= 1 - 0.45 * clamp01(idlenessValue);
  return gain;
}

/**
 * Propeller tip Mach number.
 *
 * The blade tip sees the vector sum of its own rotational speed and the aircraft's forward
 * speed: `M_tip = √((π·D·n)² + V²) / a`. On a 1.93 m fixed-pitch prop at 2700 rpm that is about
 * 0.80 standing still, which is exactly why a full-power run-up is so much harder-edged than
 * the same engine at cruise.
 */
export function propTipMach(
  rpm: number,
  diameterM: number,
  trueAirspeedMps: number,
  speedOfSoundMps: number,
): number {
  if (!(speedOfSoundMps > 0)) return 0;
  const revPerSecond = Math.max(0, rpm) / 60;
  const tipSpeed = Math.PI * diameterM * revPerSecond;
  return Math.hypot(tipSpeed, trueAirspeedMps) / speedOfSoundMps;
}

/**
 * How much blade slap to mix in, 0…1.
 *
 * Slap is the hard periodic chop of a heavily loaded propeller: loud on the ramp at run-up
 * power, absent in cruise. Two terms, exactly as the ear hears it — the tip has to be going fast
 * enough for the blade-pass tone to have any bite, and the aircraft has to be going slowly
 * enough that airflow noise is not burying it. Take off and it fades out on its own as the
 * airspeed comes up, which is the right behaviour and falls out of the second term for free.
 */
export function bladeSlapGain(tipMach: number, indicatedAirspeedMps: number): number {
  const bite = smoothstep(0.62, 0.85, tipMach);
  const unmasked = 1 - smoothstep(15, 45, indicatedAirspeedMps);
  return bite * unmasked;
}

/**
 * Centre frequency of the intake/exhaust noise band [Hz].
 *
 * Tracks shaft speed, because the induction resonance does. Bounded well inside the band the
 * ear places "engine" in, so that a sudden RPM change moves the timbre without ever sounding
 * like a filter sweep.
 */
export function intakeCentreHz(rpm: number, ratedRpm: number): number {
  const fraction = clamp01(rpm / Math.max(1, ratedRpm));
  return 260 + 900 * fraction;
}

/** Gain of the intake/exhaust noise layer, 0…1. Mostly load, with a floor so idle still breathes. */
export function intakeGain(load: number, rpmFraction: number): number {
  return clamp01(0.1 * clamp01(rpmFraction) + 0.75 * clamp01(load));
}

// ---------------------------------------------------------------------------------------------
// Airframe
// ---------------------------------------------------------------------------------------------

/** Airspeed at which the wind layer reaches unity gain [m/s] — about 117 kt, a trainer's cruise. */
export const WIND_REFERENCE_MPS = 60;

/**
 * Wind-noise amplitude, 0…~1.4.
 *
 * Aeroacoustic noise from a body in a flow rises steeply with speed — sound *power* goes as V⁶
 * for a dipole source, so amplitude goes as V³. That is very nearly unlistenable as a game curve
 * (it means nothing at all below 40 kt and everything above 120), so this uses V^2.4, which keeps
 * the character — the sound of speed, not the sound of loudness — while leaving the low end
 * audible.
 */
export function windGain(indicatedAirspeedMps: number): number {
  const v = Math.max(0, indicatedAirspeedMps) / WIND_REFERENCE_MPS;
  return Math.min(1.4, Math.pow(v, 2.4));
}

/** Lowpass cutoff for the wind layer [Hz]. Faster air is *brighter* air, not just louder air. */
export function windCutoffHz(indicatedAirspeedMps: number): number {
  return clamp(320 + 42 * Math.max(0, indicatedAirspeedMps), 320, 4200);
}

/**
 * Where the aircraft is on its way to the stall, as a fraction of the wing's own stall angle.
 *
 * 1.0 means "at the angle where this wing's lift peaks". Above 1 it is stalled. Negative alpha
 * folds to 0: this drives a warning system, and there is no such thing as being 30 % of the way
 * to a stall while pushing over.
 */
export function stallProximity(alphaRad: number, stallAlphaRad: number): number {
  if (!(stallAlphaRad > 0)) return 0;
  return Math.max(0, alphaRad / stallAlphaRad);
}

/** Proximity at which the pre-stall buffet starts to be felt. */
export const BUFFET_ONSET_PROXIMITY = 0.68;
/** Proximity at which the buffet is at full strength. */
export const BUFFET_FULL_PROXIMITY = 0.92;
/** Proximity at which the stall horn sounds. */
export const HORN_ON_PROXIMITY = 0.8;
/** Proximity at which it stops. Below {@link HORN_ON_PROXIMITY}, so it does not chatter. */
export const HORN_OFF_PROXIMITY = 0.74;

/**
 * Strength of the pre-stall buffet layer, 0…1.
 *
 * Deliberately begins *below* {@link HORN_ON_PROXIMITY}: the burble through the airframe is the
 * first warning a pilot gets, and it should be the first warning here. `model.test.ts` asserts
 * that ordering rather than leaving it to whoever next edits these constants.
 *
 * The horn's own threshold is not arbitrary either. Lift coefficient goes as α and speed as
 * 1/√CL, so a horn set at 0.80 of stall alpha sounds at `1/√0.80 = 1.12` of stall speed — six
 * knots above a 48 kt stall, which is where a C172's vane is actually rigged.
 */
export function buffetGain(proximity: number): number {
  return smoothstep(BUFFET_ONSET_PROXIMITY, BUFFET_FULL_PROXIMITY, proximity);
}

/**
 * Should the stall horn be sounding? Schmitt trigger — `wasOn` is the caller's previous answer.
 *
 * Hysteresis rather than a bare threshold because a real vane sits in turbulent air and an
 * aeroplane held right at the warning speed would otherwise produce a stutter that sounds like a
 * bug and is genuinely hard to ignore.
 */
export function stallHornActive(proximity: number, wasOn: boolean): boolean {
  return wasOn ? proximity > HORN_OFF_PROXIMITY : proximity >= HORN_ON_PROXIMITY;
}

/** Centre frequency of the buffet band [Hz] — separated flow off the wing arriving at the tail. */
export const BUFFET_CENTRE_HZ = 165;
/** Shudder rate of the buffet [Hz]. Slow enough to feel as a rhythm rather than a pitch. */
export const BUFFET_SHUDDER_HZ = 14;

// ---------------------------------------------------------------------------------------------
// Ground
// ---------------------------------------------------------------------------------------------

/** How a surface sounds under a rolling tyre. All fields are dimensionless except the cutoff. */
export interface SurfaceVoice {
  /** Lowpass cutoff for the rumble band [Hz]. Loose surfaces are darker. */
  cutoffHz: number;
  /** Overall level, 0…1. */
  gain: number;
  /** How irregular the rumble is, 0…1 — drives a random amplitude wobble on top. */
  roughness: number;
}

/**
 * The rumble voice for each surface OurAirports knows about.
 *
 * These are ordered by what they actually sound like from the cockpit rather than by how smooth
 * they are to taxi on. Concrete is *brighter* than asphalt and noticeably lumpier, because a
 * concrete runway is poured in slabs and the tyres cross an expansion joint every few metres —
 * on a long landing roll that periodicity is the most recognisable thing about it. Gravel is
 * bright and violent; grass is dark, loud and soft-edged; water is the float-plane case and is
 * mostly a low hiss.
 */
const SURFACE_VOICES: Readonly<Record<string, SurfaceVoice>> = Object.freeze({
  asphalt: { cutoffHz: 900, gain: 0.55, roughness: 0.1 },
  concrete: { cutoffHz: 1150, gain: 0.6, roughness: 0.22 },
  grass: { cutoffHz: 420, gain: 0.78, roughness: 0.55 },
  gravel: { cutoffHz: 1650, gain: 0.85, roughness: 0.85 },
  dirt: { cutoffHz: 500, gain: 0.72, roughness: 0.5 },
  water: { cutoffHz: 300, gain: 0.32, roughness: 0.3 },
  unknown: { cutoffHz: 800, gain: 0.55, roughness: 0.3 },
});

/** The rumble voice for a surface; unknown names fall back to the `unknown` entry. */
export function surfaceVoice(surface: string): SurfaceVoice {
  return SURFACE_VOICES[surface] ?? SURFACE_VOICES['unknown'];
}

/** Ground speed at which the rolling rumble is at full level [m/s] — about 58 kt. */
export const ROLL_REFERENCE_MPS = 30;

/**
 * Rolling rumble gain, 0…1.
 *
 * Square-rooted in speed so that walking-pace taxi is clearly audible instead of silent — the
 * physical noise really does go roughly linearly with speed, but the interesting information for
 * a pilot ("am I moving?") is at the bottom of the range, and a linear law throws it away.
 *
 * Scaled by weight on wheels so that the rumble *lifts off* with the aeroplane: as the wing takes
 * the load through the rotation, the wheels go quiet before they leave the ground, which is
 * exactly what happens and is a surprisingly strong cue that you are flying.
 */
export function rollGain(
  groundSpeedMps: number,
  weightOnWheelsN: number,
  referenceWeightN: number,
): number {
  if (!(referenceWeightN > 0)) return 0;
  const speed = Math.sqrt(clamp01(Math.max(0, groundSpeedMps) / ROLL_REFERENCE_MPS));
  const load = clamp01(weightOnWheelsN / referenceWeightN);
  return speed * load;
}

/** Sink rate at which the touchdown thump saturates [m/s]. Above this it cannot get worse. */
export const TOUCHDOWN_REFERENCE_SINK_MPS = 3.5;

/**
 * Loudness of the touchdown thump, 0…1, from the sink rate at the moment of contact.
 *
 * The exponent below 1 is the whole point: a greaser at 0.3 m/s still makes a small noise
 * (0.18 rather than 0.09), because a landing you barely felt is not a landing you did not hear.
 * A firm arrival at 2 m/s lands at 0.66, and anything you would apologise for pegs at 1.
 */
export function touchdownGain(sinkRateMps: number): number {
  const normalised = clamp01(Math.max(0, sinkRateMps) / TOUCHDOWN_REFERENCE_SINK_MPS);
  return Math.pow(normalised, 0.7);
}

/**
 * Loudness of the tyre chirp at touchdown, 0…1.
 *
 * A different quantity from the thump and driven by a different one: the chirp is stationary
 * rubber being spun up to ground speed, so it scales with how fast you are going *along* the
 * runway, not with how hard you arrived. A fast, smooth wheel landing chirps loudly and thumps
 * quietly; a slow, hard one does the opposite.
 */
export function touchdownChirpGain(groundSpeedMps: number): number {
  return clamp01(Math.max(0, groundSpeedMps) / 40);
}

// ---------------------------------------------------------------------------------------------
// Mix, perspective and propagation
// ---------------------------------------------------------------------------------------------

/**
 * The gain a bus should carry, from its own slider, the master slider and the mute flag.
 *
 * Squared, because a linear volume slider does not sound linear: perceived loudness is roughly
 * the square of amplitude over the useful range, so a slider at half way should be about a
 * quarter of the amplitude if moving it is to feel even.
 */
export function busGain(busVolume: number, masterVolume: number, muted: boolean): number {
  if (muted) return 0;
  const bus = clamp01(busVolume);
  const master = clamp01(masterVolume);
  return bus * bus * master * master;
}

/**
 * Cabin lowpass cutoff [Hz] for one bus, blended between outside and inside the aeroplane.
 *
 * `cockpitFraction` is 0 in an external view and 1 in the cockpit; it is a fraction rather than
 * a boolean because the camera *blends* between views (see `render/cameraRig.ts`) and a mix that
 * switched instantly while the picture was still moving would be the most obvious artefact in
 * the whole system.
 *
 * Blended geometrically rather than linearly. Cutoffs are perceived logarithmically, and a
 * linear ramp from 20 kHz to 1.4 kHz spends most of its travel in the top octave where nothing
 * audible happens and then falls off a cliff at the end.
 *
 * `cloudiness` closes it a little further — flying through the inside of a cumulus muffles the
 * world by a few hundred hertz. It is a small effect on purpose: it is atmosphere, not an event.
 */
export function cabinCutoffHz(
  openCutoffHz: number,
  closedCutoffHz: number,
  cockpitFraction: number,
  cloudiness = 0,
): number {
  const t = clamp01(cockpitFraction);
  const blended = openCutoffHz * Math.pow(closedCutoffHz / openCutoffHz, t);
  return blended * (1 - 0.16 * clamp01(cloudiness));
}

/** Speed of sound at sea level, ISA [m/s]. The atmosphere model supplies the real one in flight. */
export const SEA_LEVEL_SPEED_OF_SOUND_MPS = 340.3;

/**
 * The Doppler frequency ratio to apply to a source.
 *
 * `closingSourceMps` is the source's velocity component **toward the listener**, and
 * `closingListenerMps` the listener's velocity component **toward the source** — both signed, in
 * m/s, and both positive when that party is closing the gap. In those terms the standard result
 *
 *     f'/f = (c + v_observer_toward_source) / (c − v_source_toward_observer)
 *
 * says exactly what it looks like it says: anything closing raises the pitch and anything opening
 * lowers it, and there is no sign to get backwards.
 *
 * Two properties worth knowing, both asserted by the tests:
 *   - a listener travelling *with* the source — one closing at exactly the rate the other opens,
 *     so `closingListener = −closingSource` — gets exactly 1, which is why the chase camera hears
 *     no shift at all;
 *   - the ratio is clamped to ±1 octave, which no subsonic aeroplane can reach anyway but which
 *     stops a numerical blow-up at the denominator from turning into a scream.
 */
export function dopplerRatio(
  closingSourceMps: number,
  closingListenerMps: number,
  speedOfSoundMps = SEA_LEVEL_SPEED_OF_SOUND_MPS,
): number {
  const c = Math.max(1, speedOfSoundMps);
  const denominator = c - clamp(closingSourceMps, -0.9 * c, 0.9 * c);
  if (!(denominator > 1e-3)) return 2;
  const ratio = (c + clamp(closingListenerMps, -0.9 * c, 0.9 * c)) / denominator;
  return clamp(ratio, 0.5, 2);
}

/** Distance at which a source is at full level [m] — roughly "in the cockpit with it". */
export const DISTANCE_REFERENCE_M = 15;

/**
 * Distance attenuation, 0…1.
 *
 * The inverse-distance law with a rolloff factor of 0.5 rather than the physical 1. Strict
 * inverse-distance is correct for a point source in free air and wrong for this: an aeroplane is
 * not a point, the ear's loudness response is compressive, and a fly-by at 300 m that is only 4 %
 * as loud as one at 15 m simply is not there. Half rolloff keeps the *shape* — the swell and fade
 * of a pass — while leaving something to hear at the ends of it.
 */
export function distanceGain(distanceM: number, rolloff = 0.5): number {
  const d = Math.max(0, distanceM);
  if (d <= DISTANCE_REFERENCE_M) return 1;
  return DISTANCE_REFERENCE_M / (DISTANCE_REFERENCE_M + rolloff * (d - DISTANCE_REFERENCE_M));
}

/**
 * Air-absorption lowpass cutoff [Hz] at a given distance.
 *
 * Air eats high frequencies with range, which is why a distant aeroplane is a drone and a close
 * one is a snarl. An exponential decay with a 1.8 km scale height gets the character right
 * without pretending to be a real atmospheric absorption model (which depends on humidity and
 * temperature and would be false precision here).
 */
export function distanceCutoffHz(distanceM: number): number {
  return clamp(20_000 * Math.exp(-Math.max(0, distanceM) / 1800), 900, 20_000);
}

// ---------------------------------------------------------------------------------------------
// Ambience
// ---------------------------------------------------------------------------------------------

/**
 * Level of the ambient wind bed, 0…1.
 *
 * This is the wind you hear *around* you when parked, not the wind you hear *past* you when
 * flying, and the two must not both be running: as soon as the airframe layer takes over the
 * ambient bed has to get out of the way or the mix turns to mush. Hence the duck against
 * indicated airspeed, which is fully closed by 25 m/s (about 50 kt).
 */
export function ambientWindGain(surfaceWindMps: number, indicatedAirspeedMps: number): number {
  const strength = clamp01(surfaceWindMps / 12);
  const duck = 1 - smoothstep(4, 25, indicatedAirspeedMps);
  return strength * duck;
}

/**
 * Level of the rain-on-airframe hiss, 0…1.
 *
 * Scales with airspeed as well as intensity, because what you hear in the cabin is drops hitting
 * the windshield and the wing skin, and how hard they hit is how fast you are going through
 * them. Parked in the rain it is a patter; at 120 kt it is a roar.
 */
export function rainGain(precipitation: number, indicatedAirspeedMps: number): number {
  const intensity = clamp01(precipitation);
  const impact = 0.25 + 0.75 * clamp01(indicatedAirspeedMps / WIND_REFERENCE_MPS);
  return intensity * impact;
}

/**
 * Level of the birdsong bed, 0…1.
 *
 * On only when the aeroplane is parked with the engine stopped, which is the one moment in a
 * flight when the world is quiet enough to hear anything else. It fades out on the *starter*,
 * not on the throttle, so the first thing the start sequence does is take the birds away.
 */
export function birdsongGain(
  onGround: boolean,
  engineRunning: boolean,
  rpm: number,
  groundSpeedMps: number,
): number {
  if (!onGround || engineRunning) return 0;
  const stopped = 1 - smoothstep(20, 220, rpm);
  const still = 1 - smoothstep(0.5, 4, groundSpeedMps);
  return stopped * still;
}

// ---------------------------------------------------------------------------------------------
// Turbine and ordnance — M6's voices, modelled now so the synths are real rather than promised
// ---------------------------------------------------------------------------------------------

/**
 * Compressor whine frequency [Hz] at a given fan speed.
 *
 * The whine you hear from a turbofan is the fan's blades passing the inlet guide vanes: with a
 * fan turning at up to about 300 rev/s and blade counts in the tens, that lands in the 2–5 kHz
 * range and rises steeply with N1. It never goes to zero — a spooled-down engine still has a fan
 * windmilling — so the curve starts at an idle whistle rather than at silence.
 */
export function turbineWhineHz(n1: number, bladeCount = 22): number {
  const fraction = clamp01(n1);
  const revPerSecond = 45 + 255 * fraction;
  return revPerSecond * bladeCount * 0.4;
}

/**
 * Level of the turbine's broadband roar, 0…1.
 *
 * Combustion and jet-exhaust mixing noise, which unlike the whine is genuinely absent at idle and
 * dominant at power. Cubed rather than linear because jet noise power goes roughly as the eighth
 * power of exhaust velocity — the real curve is far steeper than anything usable, and a cube is
 * the compromise that still makes the difference between 80 % and 100 % N1 dramatic.
 */
export function turbineRoarGain(n1: number): number {
  const fraction = clamp01(n1);
  return fraction * fraction * fraction;
}

/**
 * Level of the afterburner layer, 0…1, from the nozzle stage.
 *
 * Deliberately not a fade from zero. Reheat lights as an *event* — the fuel ignites in the jet
 * pipe and the noise arrives with it — so the curve steps hard at the bottom of its travel and
 * then grows slowly across the rest of the stage.
 */
export function afterburnerGain(stage: number): number {
  const s = clamp01(stage);
  if (s <= 0.01) return 0;
  return 0.55 + 0.45 * s;
}

/**
 * Advance a spool state toward its commanded value.
 *
 * First-order lag, and the asymmetry is the whole character of a jet: spooling *up* from idle
 * takes several seconds because the compressor has to accelerate against its own aerodynamic
 * load, and spooling down is faster because the load is doing the work. A symmetric lag sounds
 * like a synthesiser portamento; this sounds like an engine.
 */
export function spoolToward(
  current: number,
  commanded: number,
  dtSeconds: number,
  spoolUpS = 4.5,
  spoolDownS = 2.5,
): number {
  const target = clamp01(commanded);
  const tau = target > current ? spoolUpS : spoolDownS;
  const alpha = 1 - Math.exp(-Math.max(0, dtSeconds) / Math.max(1e-3, tau));
  return current + (target - current) * alpha;
}

/**
 * Lowpass cutoff for an explosion at a given distance [Hz].
 *
 * The same air absorption as {@link distanceCutoffHz}, but biased far lower: an explosion at
 * range is the canonical example of the effect, and a distant one really is nothing but the
 * bottom two octaves. The floor at 180 Hz is what keeps a horizon-line explosion as a felt thud
 * rather than removing it entirely.
 */
export function explosionCutoffHz(distanceM: number): number {
  return clamp(14_000 * Math.exp(-Math.max(0, distanceM) / 700), 180, 14_000);
}
